package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.GivingSummary
import com.dotdrives.repository.GivingSummaryRepository
import com.dotdrives.model.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class GivingSummaryUiState(
    val loading: Boolean = false,
    /** True for a field-role viewer -- [GivingSummaryController.load] never issues the
     *  underlying `donations` query in that case; the presentation layer renders a restricted
     *  state instead of an empty one, matching [Report]'s "no data yet" vs "no results" rule. */
    val restricted: Boolean = false,
    val year: Int = 0,
    val targetId: String? = null,
    val stewardUserId: String? = null,
    val periodKind: ReportPeriodKind = ReportPeriodKind.FiscalYear,
    val period: ReportPeriod? = null,
    val summary: GivingSummary? = null,
    val targetCatalog: List<TargetGivingSummary> = emptyList(),
    val stewardCatalog: List<StewardGivingSummary> = emptyList(),
    val error: String? = null,
)

/**
 * The Reports (Summaries) desktop frame. `donations` RLS is `has_perm_strict('donation.read')`,
 * which excludes the `field` role entirely (docs/schema.md: "Donations ... Field reps get zero
 * rows back from any table here, on read or write"). [isFieldRep] gates [load] itself -- for a
 * field-role viewer this controller never calls [GivingSummaryRepository.load] at all, which is
 * the strongest available guarantee that a field rep cannot reach donor financials through this
 * screen: not "the server hides the numbers," but "the client never asks."
 */
class GivingSummaryController(
    private val repo: GivingSummaryRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val isFieldRep: Boolean,
    initialYear: Int,
    private val fiscalStartMonth: Int = 1,
    private val fiscalStartDay: Int = 1,
) : DotController<GivingSummaryUiState>(GivingSummaryUiState(year = initialYear), scope) {
    private var generation = 0L

    fun load() {
        if (isFieldRep) {
            update { it.copy(restricted = true, loading = false, error = null, summary = null) }
            return
        }
        val current = nextGeneration()
        // Captured now, synchronously, not re-read from `state.value` inside the coroutine
        // below: a second load() (e.g. a fast setTarget() right after setYear()) mutates state
        // before this coroutine gets scheduled, which would otherwise send the *new* filters
        // under the *old* generation's already-superseded request instead of the ones this
        // call was actually made for.
        val snapshot = state.value
        val filter = ReportFilter(snapshot.period ?: fiscalYearPeriod(snapshot.year, fiscalStartMonth, fiscalStartDay), snapshot.periodKind, snapshot.targetId, snapshot.stewardUserId)
        update { it.copy(loading = true, error = null, summary = null) }
        scope.launch {
            try {
                repo.load(orgId, filter)
                    .onSuccess { summary ->
                        update { if (isCurrent(current)) it.copy(loading = false, summary = summary, targetCatalog=summary.targetCatalog, stewardCatalog=summary.stewardCatalog, error = null) else it }
                    }
                    .onFailure { publishError(it, current) }
            } catch (error: Exception) {
                publishError(error, current)
            }
        }
    }

    fun retry() = load()

    fun setYear(year: Int) {
        update { it.copy(year = year, periodKind = ReportPeriodKind.FiscalYear, period = fiscalYearPeriod(year, fiscalStartMonth, fiscalStartDay)) }
        load()
    }

    fun setTarget(targetId: String?) {
        update { it.copy(targetId = targetId) }
        load()
    }

    fun setSteward(userId: String?) { update { it.copy(stewardUserId = userId) }; load() }
    fun setPeriod(kind: ReportPeriodKind, period: ReportPeriod) { update { it.copy(periodKind = kind, period = period) }; load() }
    fun setFilter(filter: ReportFilter, fiscalYear: Int = state.value.year) {
        update { it.copy(year=fiscalYear,periodKind=filter.kind,period=filter.period,targetId=filter.targetId,stewardUserId=filter.stewardUserId) }
        load()
    }

    private fun publishError(error: Throwable, generationAtCall: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generationAtCall)) return
        update {
            if (isCurrent(generationAtCall)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it
        }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(generationAtCall: Long): Boolean = generationAtCall == generation
}
